const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };
  return date.toLocaleDateString('en-US', options);
};

const downloadCsv = async (data, headers, fields, filename, price) => {
  const csvContent =
    'data:text/csv;charset=utf-8,' +
    headers.join(',') +
    '\n' +
    data
      .map((row) =>
        fields
          .map((field) => {
            if (field === 'createdAt') {
              return formatDate(row[field]).replace(', ', ' ');
            } else if (field === 'price') {
              return `$${price}`;
            } else {
              return row[field];
            }
          })
          .join(','),
      )
      .join('\n');

  const currentTimestamp = new Date().getTime();
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${filename}-${currentTimestamp}.csv`);
  document.body.appendChild(link);
  link.click();
};

const resolveTotalUnits = (order) => {
  if (order.units != null) return order.units;
  if (order.totalQuantity != null) return order.totalQuantity;
  if (order.unitCount != null) return order.unitCount;
  if (order.totalUnits != null) return order.totalUnits;
  if (Array.isArray(order.lineItems) && order.lineItems.length > 0) {
    return order.lineItems.reduce((sum, li) => sum + (li.quantity ?? 0), 0);
  }
  if (Array.isArray(order.shipments) && order.shipments.length > 0) {
    const total = order.shipments.reduce((sum, s) => {
      if (!Array.isArray(s.lineItems)) return sum;
      return sum + s.lineItems.reduce((inner, li) => inner + (li.quantity ?? 0), 0);
    }, 0);
    if (total > 0) return total;
  }
  return null;
};

const formatFee = (val) => (val != null ? `$${Number(val).toFixed(2)}` : '—');
const formatRate = (val) => (val != null ? `$${Number(val).toFixed(3)}` : '—');
const formatStorageRate = formatRate;
const formatEstimated = (val) => (val != null ? `~$${Number(val).toFixed(2)}` : '—');
const formatLedgerUnitFee = (val, eventType) =>
  eventType === 'Storage' ? formatRate(val) : formatFee(val);

// Parse a possibly date-only value ("YYYY-MM-DD") as *local* midnight so date
// math doesn't shift a day in UTC-negative timezones. Full ISO timestamps also
// resolve to local midnight of their date component, which is what due-date
// arithmetic wants.
const parseDateLocal = (val) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(val));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(val);
};

// Resolve a logo/asset URL returned by the backend.
//
// New contract (Task #686): logos are stored as DB blobs and served from
// /api/logos/<id> — the backend returns absolute URLs, so they pass through
// unchanged and load cross-origin without any path prefixing.
//
// Old /uploads/logos/... relative paths are dead: the public host never
// forwarded /uploads, so they always served the SPA page. Treat them as
// "no logo" (return null) instead of constructing an unreachable absolute URL.
//
// External https:// URLs and data: URIs are returned as-is.
const resolveAssetUrl = (url) => {
  if (!url) return null;
  // Degrade dead legacy upload paths to "no logo".
  if (url.startsWith('/uploads/logos/')) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  // Remaining relative paths: prefix with the API origin.
  const apiBase = process.env.REACT_APP_API ?? '';
  const origin = apiBase.replace(/\/api\/?$/, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

export {
  formatDate,
  downloadCsv,
  resolveTotalUnits,
  formatFee,
  formatRate,
  formatStorageRate,
  formatEstimated,
  formatLedgerUnitFee,
  parseDateLocal,
  resolveAssetUrl,
};
